import { useState, useEffect } from 'react';
import { useQuery } from 'react-query';
import isNil from 'lodash/isNil.js';
import { useFirebase } from 'components/Firebase/index.js';
import useOrgId from 'hooks/useOrgId.js';
import { productCatalogueServerUrl } from '../../../client-config.js';

// Supports two calling styles:
// 1) useProducts({ page, itemsPerPage, search, feedId, sort, debounceMs, ...queryOptions })
// 2) useProducts({ queryParams: { q, page, limit, feedId, sort }, debounceMs, ...queryOptions })
const useProducts = (options = {}) => {
  const firebase = useFirebase();
  const orgId = useOrgId();

  const { queryParams: inputQueryParams, debounceMs = 150, ...queryOptions } = options;

  // Map legacy flat options to query params expected by the API
  const { q, page, limit, feedId, sort } = inputQueryParams ?? {
    q: options.search,
    page: options.page,
    limit: options.itemsPerPage,
    feedId: options.feedId,
    sort: options.sort,
  };

  const [debouncedParams, setDebouncedParams] = useState({ q, page, limit, feedId, sort });

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedParams({ q, page, limit, feedId, sort });
    }, debounceMs);

    return () => {
      clearTimeout(handler);
    };
  }, [q, page, limit, feedId, sort, debounceMs]);

  return useQuery({
    queryKey: ['products', orgId, debouncedParams],
    queryFn: async ({ signal }) => {
      const validParams = Object.entries(debouncedParams || {}).filter(([, value]) => !isNil(value));

      const idToken = await firebase.getIdToken();
      const fetchUrl = new URL(`${productCatalogueServerUrl}/api/v1/orgs/${orgId}/products`);
      const urlParams = new URLSearchParams(validParams);
      fetchUrl.search = urlParams.toString();

      const response = await fetch(fetchUrl.toString(), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
        signal,
      });

      const { ok, status } = response;
      if (!ok) {
        throw new Error(`Failed to fetch products. Status: ${status}`);
      }

      const json = await response.json();
      // Server returns { products, total, page, limit }
      // Normalize to { items, total, page, limit } for current consumers
      return {
        items: json.products || json.items || [],
        total: json.total ?? (Array.isArray(json.products) ? json.products.length : 0),
        page: json.page,
        limit: json.limit,
      };
    },
    ...queryOptions,
    enabled: queryOptions.enabled !== false && !!orgId,
  });
};

export default useProducts;
